/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { PageHeader, PhotoTitleCard, type SplitFeatureSectionPicture } from '@repo/shared-client/components/blocks'
import { createFileRoute, Link } from '@tanstack/react-router'
import allEventsPicture from '@web/src/assets/event/event-all-events.webp?w=400;800;1200&format=avif;webp&as=picture'
import campingPicture from '@web/src/assets/event/event-camping.webp?w=400;800;1200&format=avif;webp&as=picture'
import clubEventsPicture from '@web/src/assets/event/event-club-events.webp?w=400;800;1200&format=avif;webp&as=picture'
import cruisesPicture from '@web/src/assets/event/event-cruises.webp?w=400;800;1200&format=avif;webp&as=picture'
import housePartiesPicture from '@web/src/assets/event/event-house-parties.webp?w=400;800;1200&format=avif;webp&as=picture'
import kinkPicture from '@web/src/assets/event/event-kink.webp?w=400;800;1200&format=avif;webp&as=picture'
import noClubEventsPicture from '@web/src/assets/event/event-no-club-events.webp?w=400;800;1200&format=avif;webp&as=picture'
import otherPicture from '@web/src/assets/event/event-other.webp?w=400;800;1200&format=avif;webp&as=picture'
import socialAndMeetPicture from '@web/src/assets/event/event-social-and-meet.webp?w=400;800;1200&format=avif;webp&as=picture'
import takeoversPicture from '@web/src/assets/event/event-takeovers.webp?w=400;800;1200&format=avif;webp&as=picture'
import vacationPicture from '@web/src/assets/event/event-vacation.webp?w=400;800;1200&format=avif;webp&as=picture'
import EventsMenu from '@web/src/features/event/events-menu/events-menu'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import type { EventCategoryEnum } from 'types/graphql'
import { eventCategoriesIndexHeadFromLoaderData } from './index.seo'

/** All list categories except `CLUBS` — used for “Non-Club Events” navigation (align with `EVENT_CATEGORIES_EXCEPT_CLUBS` in `./index.seo.ts`). */
const EVENT_CATEGORIES_EXCEPT_CLUBS: EventCategoryEnum[] = [
  'KINK',
  'CAMPING',
  'HOUSE_PARTIES',
  'SOCIAL_AND_MEET',
  'TAKEOVER',
  'CRUISES',
  'VACATION',
  'OTHER',
]

type EventCategoryCountsQuery = {
  eventCategoryCounts: {
    allEvents: number
    camping: number
    clubs: number
    kink: number
    houseParties: number
    socialAndMeet: number
    takeover: number
    cruises: number
    vacation: number
    other: number
    noClubEvents: number
  }
}

const EVENT_CATEGORY_COUNTS_QUERY: TypedDocumentNode<EventCategoryCountsQuery> = gql`
  query EventCategoryCountsQuery {
    eventCategoryCounts {
      allEvents
      camping
      clubs
      kink
      houseParties
      socialAndMeet
      takeover
      cruises
      vacation
      other
      noClubEvents
    }
  }
`

export const Route = createFileRoute('/(public)/(main-layout)/event/')({
  component: EventCategoryTilesPage,
  head: ({ loaderData }) => eventCategoriesIndexHeadFromLoaderData(loaderData),
  loader: async ({ context }) => {
    const { queryData, queryRef } = await queryAndPreload(context, EVENT_CATEGORY_COUNTS_QUERY)
    return {
      eventCategoryCountsQueryRef: queryRef,
      eventCategoryCountsQueryData: queryData,
      eventCategoryCounts: queryData?.eventCategoryCounts ?? null,
    }
  },
})

type CategoryCardConfig = {
  id: string
  titleKey: string
  imagePicture: SplitFeatureSectionPicture
  imageY?: string | number
  count: number
  category?: EventCategoryEnum
  /** Multiple categories in URL (comma-separated); wins over `category` when both set. */
  categories?: EventCategoryEnum[]
  /** When set, passed as `distance` in `/event/list` search (omit for list defaults). */
  distance?: number
  desktopSpan?: { colSpan?: number; rowSpan?: number }
}

function EventCategoryTilesPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const { data } = useReadQuery(loaderData.eventCategoryCountsQueryRef as QueryRef<EventCategoryCountsQuery>)
  const counts =
    (data ?? loaderData.eventCategoryCountsQueryData)?.eventCategoryCounts ?? loaderData.eventCategoryCounts

  /** Keep tile ids, categories, and multi-category list aligned with `CATEGORY_TILES_FOR_JSON_LD` in `./index.seo.ts`. */
  const cards: CategoryCardConfig[] = [
    {
      id: 'all-events',
      titleKey: t('All Events'),
      imagePicture: allEventsPicture,
      count: counts?.allEvents ?? 0,
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'no-club-events',
      titleKey: t('Non-Club Events'),
      imagePicture: noClubEventsPicture,
      count: counts?.noClubEvents ?? 0,
      categories: EVENT_CATEGORIES_EXCEPT_CLUBS,
      imageY: '30%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'camping',
      titleKey: t('Camping'),
      imagePicture: campingPicture,
      count: counts?.camping ?? 0,
      category: 'CAMPING',
      imageY: '100%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'clubs',
      titleKey: t('Club Events'),
      imagePicture: clubEventsPicture,
      count: counts?.clubs ?? 0,
      category: 'CLUBS',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'cruises',
      titleKey: t('Cruises'),
      imagePicture: cruisesPicture,
      count: counts?.cruises ?? 0,
      category: 'CRUISES',
      imageY: '80%',
      desktopSpan: { colSpan: 2, rowSpan: 2 },
    },
    {
      id: 'house-parties',
      titleKey: t('House Parties'),
      imagePicture: housePartiesPicture,
      count: counts?.houseParties ?? 0,
      category: 'HOUSE_PARTIES',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'kink',
      titleKey: t('Kink'),
      imagePicture: kinkPicture,
      count: counts?.kink ?? 0,
      category: 'KINK',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
    {
      id: 'other',
      titleKey: t('Other'),
      imagePicture: otherPicture,
      count: counts?.other ?? 0,
      category: 'OTHER',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'social-and-meet',
      titleKey: t('Social & Meetups'),
      imagePicture: socialAndMeetPicture,
      count: counts?.socialAndMeet ?? 0,
      category: 'SOCIAL_AND_MEET',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'takeover',
      titleKey: t('Takeovers'),
      imagePicture: takeoversPicture,
      count: counts?.takeover ?? 0,
      category: 'TAKEOVER',
      imageY: '65%',
      desktopSpan: { colSpan: 1, rowSpan: 2 },
    },
    {
      id: 'vacation',
      titleKey: t('Vacations'),
      imagePicture: vacationPicture,
      count: counts?.vacation ?? 0,
      category: 'VACATION',
      imageY: '70%',
      desktopSpan: { colSpan: 1, rowSpan: 1 },
    },
  ]

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pt-2 pb-10 md:px-16 md:pt-4 md:pb-16">
      <PageHeader
        breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Event Categories') }]}
        highlightTitle={t('Categories')}
        preTitle={t('Event')}
        subtitle={t('Browse event categories to quickly jump into events that match your vibe.')}
      />

      <div className="my-4">
        <EventsMenu activeTab="categories" />
      </div>

      <div className="grid w-full auto-rows-auto grid-cols-1 gap-3 pt-4 md:auto-rows-[180px] md:grid-cols-3 md:gap-6 md:pt-0">
        {cards.map((card) => {
          const categoriesParam = card.categories?.join(',') ?? (card.category ? card.category : undefined)
          return (
            <div
              className={[
                'col-span-1 row-span-1 h-full',
                (card.desktopSpan?.colSpan ?? 1) === 2 ? 'md:col-span-2' : 'md:col-span-1',
                (card.desktopSpan?.rowSpan ?? 1) === 2 ? 'md:row-span-2' : 'md:row-span-1',
              ].join(' ')}
              key={card.id}
            >
              <PhotoTitleCard
                imageAlt={t('Background image for {{title}}', { title: card.titleKey })}
                imagePicture={card.imagePicture}
                imagePositionY={card.imageY}
                rootProps={{
                  as: Link,
                  className: 'block w-full',
                  search: {
                    page: 1,
                    ...(categoriesParam ? { categories: categoriesParam } : {}),
                    ...(card.distance != null ? { distance: card.distance } : {}),
                  },
                  to: '/event/list',
                }}
                subtitle={t('{{count}} Events', { count: card.count })}
                title={card.titleKey}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
